import { NO_BROWSER_AUTOFILL } from "./browserAutofill";

export const ALLOW_BROWSER_AUTOFILL_ATTR = "data-allow-browser-autofill";

const PROCESSED_ORIG_ID = "data-mqorva-autofill-id";
const PROCESSED_OPAQUE_ID = "data-mqorva-opaque-id";
const PROCESSED_ORIG_NAME = "data-mqorva-autofill-name";
const SENTINEL_ATTR = "data-mqorva-autofill-sentinel";

const SKIP_CONTROL_TYPES = new Set([
  "hidden",
  "checkbox",
  "radio",
  "file",
  "button",
  "submit",
  "reset",
  "image",
  "range",
  "color"
]);

/** Öffentliche Selbstbedienung und Anmelden: dort darf der Browser speichern. */
const ALLOW_PATH_PREFIXES = [
  "/login",
  "/admin/login",
  "/reset-password",
  "/request-password-reset",
  "/book/",
  "/cancel-booking",
  "/lead/",
  "/contract-widget/",
  "/public/"
];

export function currentUiPath(): string {
  if (typeof window === "undefined") return "/";
  const href = String(window.location.href || "");
  const hashIndex = href.indexOf("#");
  if (hashIndex >= 0) {
    const raw = href.slice(hashIndex + 1);
    const pathAndQuery = raw.startsWith("/") ? raw : `/${raw}`;
    const path = pathAndQuery.split("?")[0] || "/";
    return path.startsWith("/") ? path : `/${path}`;
  }
  return window.location.pathname || "/";
}

export function pathAllowsBrowserAutofill(pathname: string): boolean {
  const path = pathname || "/";
  return ALLOW_PATH_PREFIXES.some((prefix) => (prefix.endsWith("/") ? path.startsWith(prefix) : path === prefix || path.startsWith(`${prefix}/`)));
}

export function autocompleteAllowsBrowserSave(autoComplete: string | null | undefined): boolean {
  if (!autoComplete) return false;
  const tokens = autoComplete.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.includes("username") || tokens.includes("current-password") || tokens.includes("one-time-code")) return true;
  const hasSection = tokens.some((token) => token.startsWith("section-"));
  return hasSection && (tokens.includes("new-password") || tokens.includes("email"));
}

// FNV-1a (32 Bit): Startwert und Primzahl-Multiplikator.
const FNV_OFFSET_BASIS = 2166136261;
const FNV_PRIME = 16777619;
const BASE36 = 36;
const AUTOCOMPLETE_OFF = "off";

export function opaqueAutofillToken(seed: string): string {
  let hash = FNV_OFFSET_BASIS;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, FNV_PRIME);
  }
  return (hash >>> 0).toString(BASE36);
}

function isAllowlistedSubtree(node: Element): boolean {
  return Boolean(node.closest(`[${ALLOW_BROWSER_AUTOFILL_ATTR}]`));
}

function applyNoFillAttributes(el: HTMLElement): void {
  el.setAttribute("autocomplete", NO_BROWSER_AUTOFILL);
  el.setAttribute("data-1p-ignore", "true");
  el.setAttribute("data-lpignore", "true");
  el.setAttribute("data-form-type", "other");
  el.setAttribute("data-mqorva-no-browser-autofill", "true");
}

function ensureScrollSentinel(input: HTMLElement, originalId: string): void {
  if (!originalId || document.getElementById(originalId)) return;
  const sentinel = document.createElement("span");
  sentinel.id = originalId;
  sentinel.setAttribute(SENTINEL_ATTR, "true");
  sentinel.setAttribute("aria-hidden", "true");
  input.parentElement?.insertBefore(sentinel, input);
}

function decoupleItemLabel(input: HTMLElement, originalId: string, opaqueLabelId: string): void {
  const item = input.closest(".ck-form-item");
  const label = item?.querySelector("label");
  if (!label) return;
  const htmlFor = label.getAttribute("for");
  if (htmlFor === originalId || htmlFor === input.id) {
    label.setAttribute("for", opaqueLabelId);
  }
}

function neutralizeControl(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): void {
  if (isAllowlistedSubtree(el) || autocompleteAllowsBrowserSave(el.getAttribute("autocomplete"))) return;
  if (el instanceof HTMLInputElement && SKIP_CONTROL_TYPES.has((el.type || "text").toLowerCase())) return;

  applyNoFillAttributes(el);

  const currentName = el.getAttribute("name");
  if (currentName && !currentName.startsWith("mqaf-") && !currentName.startsWith("mq-")) {
    if (!el.hasAttribute(PROCESSED_ORIG_NAME)) el.setAttribute(PROCESSED_ORIG_NAME, currentName);
    el.setAttribute("name", `mqaf-${opaqueAutofillToken(currentName)}`);
  }

  const recordedOrigId = el.getAttribute(PROCESSED_ORIG_ID);
  const recordedOpaqueId = el.getAttribute(PROCESSED_OPAQUE_ID);
  let originalId = recordedOrigId;
  if (!originalId) {
    const currentId = el.id;
    if (!currentId || currentId.startsWith("mqaf-") || currentId.startsWith("mq-")) return;
    originalId = currentId;
    const opaqueId = `mqaf-${opaqueAutofillToken(originalId)}`;
    el.setAttribute(PROCESSED_ORIG_ID, originalId);
    el.setAttribute(PROCESSED_OPAQUE_ID, opaqueId);
    el.id = opaqueId;
    ensureScrollSentinel(el, originalId);
    decoupleItemLabel(el, originalId, `mqaf-lbl-${opaqueAutofillToken(originalId)}`);
    return;
  }

  if (recordedOpaqueId && el.id === originalId) {
    el.id = recordedOpaqueId;
  }
  if (originalId) {
    ensureScrollSentinel(el, originalId);
    decoupleItemLabel(el, originalId, `mqaf-lbl-${opaqueAutofillToken(originalId)}`);
  }
}

function neutralizeForm(form: HTMLFormElement): void {
  if (isAllowlistedSubtree(form) || pathAllowsBrowserAutofill(currentUiPath())) return;
  if (form.getAttribute("autocomplete") === AUTOCOMPLETE_OFF) return;
  form.setAttribute("autocomplete", "off");
}

export function neutralizeInternalBrowserAutofill(root: ParentNode = document): void {
  if (typeof window === "undefined") return;
  if (pathAllowsBrowserAutofill(currentUiPath())) return;

  root.querySelectorAll("form").forEach((form) => neutralizeForm(form));
  root.querySelectorAll("input, textarea, select").forEach((node) => {
    neutralizeControl(node as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement);
  });
}

export function startInternalBrowserAutofillGuard(root: ParentNode = document): () => void {
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      neutralizeInternalBrowserAutofill(root);
    });
  };

  neutralizeInternalBrowserAutofill(root);
  const observer = new MutationObserver(schedule);
  const target = root instanceof Document ? root.documentElement : (root as Element);
  observer.observe(target, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["id", "name", "autocomplete", "for"]
  });
  window.addEventListener("hashchange", schedule);

  return () => {
    observer.disconnect();
    window.removeEventListener("hashchange", schedule);
    if (frame) window.cancelAnimationFrame(frame);
  };
}
